/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable jsx-a11y/anchor-is-valid */

import React,{FC, useEffect, useState, useMemo} from "react";
import { Card, Dropdown, Spinner, Row, Col, Pagination, Form, Button, Alert  } from "react-bootstrap";
import { AVAILABLE_YEARS, HS_DIGITS } from "../../../../libs/constant";
import { getCommodities } from "../core/_request";
import { createColumnHelper, flexRender, getCoreRowModel, getFilteredRowModel, getPaginationRowModel, getSortedRowModel, PaginationState, SortingState, useReactTable} from '@tanstack/react-table';
import { getNameByISO3, shortenLargeNumber } from "../../../../libs/misc";
import { TradeDataTable } from "../core/_models";
import { useLoading } from "../../../../contexts/LoadingContext";


type TopCommodityTableCardProps = {
  handleHSClicked:(hs:string, year:number, flow:string, hsList:string|null, description:string)=>void;
  params:any;
  product_group?: any;
}

const columnHelper = createColumnHelper<TradeDataTable>()

const TopCommodityTableCard:FC<TopCommodityTableCardProps> = ({
  params,
  handleHSClicked,
  product_group = [],
}) => {
  const [isLoading, setIsLoading] = useState(false)
  const [isNotFound, setIsNotFound] = useState(false)
  const [selectedYear, setSelectedYear] = useState(AVAILABLE_YEARS[0])
  const [selectedHSDigit, setSelectedHSDigit] = useState(HS_DIGITS[HS_DIGITS.length-1])
  const [dataTable, setDataTable] = useState<TradeDataTable[]>()
  const { registerStart, registerEnd } = useLoading();

  // Build dynamic labels and titles using useMemo for stability and performance
  const [mainTitle, subTitle] = useMemo(() => {
    const findPG = (id: number, list: any[]): any | null => {
      for (const item of list) {
        if (item.id === id) return item;
        if (item.sub_category?.length) {
          const found = findPG(id, item.sub_category);
          if (found) return found;
        }
      }
      return null;
    };

    const flowLabel = params.tradeflow === 'imports' ? 'Import' : 'Export'
    const selectedPGs = params.product_group || []
    let pgLabel = ''
    
    if (selectedPGs.length === 1) {
      const selectedPG = findPG(+selectedPGs[0], product_group);
      if (selectedPG?.sub_category?.length) {
        pgLabel = `${selectedPG.name} (Detail)`
      } else {
        pgLabel = selectedPG?.name || 'Product Group'
      }
    } else {
      const selectedNames = selectedPGs.map((pgId: any) => {
        const found = findPG(+pgId, product_group);
        return found?.name || ''
      }).filter(Boolean)
      pgLabel = selectedNames.length > 0 ? selectedNames.join(', ') : 'Product Group'
    }

    const main = `${flowLabel} Commodities: ${getNameByISO3(params.rep)} in ${selectedYear}`
    return [main, pgLabel]
  }, [params.rep, params.tradeflow, params.product_group, product_group, selectedYear])

  const cardTitle = `${mainTitle} - ${subTitle}`;

  //State for Datatable
  const [pagination, setPagination] = React.useState<PaginationState>({
        pageIndex: 0,
        pageSize: 20,
      })
  const [globalFilter, setGlobalFilter] = useState<any>([])
  const [sorting, setSorting] = React.useState<SortingState>([])

  const columns = [
    columnHelper.accessor('hs',{
        header: +selectedHSDigit.value===1?'HS Section':'HS Code',
        cell: info=>{
          const flow = info.row.original.flow
          const hsList = info.row.original?.hs_list || null
          const hs = info.getValue()
          const description = info.row.original?.description || ''
          return (
            <span 
              className="badge cursor-pointer text-primary fw-1000 " 
              style={{background:'rgba(5, 66, 148, 0.1)'}} 
              title={info.row.original?.hs_list!=='-'?`Group of: ${info.row.original?.hs_list}`:''}
              onClick={() => handleHSClicked(hs,selectedYear,flow, hsList, description)}>
              {hs}
            </span>
            )
        },
        meta: {
          headerClass: 'ps-4 min-w-90px bg-light rounded-start',
          cellClass: 'text-center'
        },
      }),
      columnHelper.accessor('description',{
        header: 'Description',
        cell: info=>info.getValue(),
        meta: {
          headerClass: 'min-w-125px bg-light',
          cellClass: 'text-start'
        },
      }),
      columnHelper.accessor('val',{
        header: "Value",
        cell: info=>shortenLargeNumber(info.getValue()),
        meta: {
          headerClass: 'min-w-90px text-end rounded-end px-3 bg-light',
          cellClass: 'text-end fw-bold px-5'
        },
      })
  ];
  const table = useReactTable({
      data:dataTable || [],
      columns,
      getCoreRowModel: getCoreRowModel(),
      getSortedRowModel: getSortedRowModel(),
      getFilteredRowModel: getFilteredRowModel(),
      getPaginationRowModel: getPaginationRowModel(),
      onPaginationChange: setPagination,
      onGlobalFilterChange: setGlobalFilter,
      onSortingChange: setSorting,
      state: {
        pagination,
        globalFilter,
        sorting
      },
    })

  function paginationRange(dataTable) {
    const currentPage = pagination.pageIndex + 1;
    const totalPages = dataTable.getPageCount();
    const maxPagesToShow = 1;
    const halfRange = Math.floor(maxPagesToShow / 2);

    // Ensure startPage and endPage are within valid range
    let startPage = Math.max(1, currentPage - halfRange);
    let endPage = Math.min(totalPages, currentPage + halfRange);

    // Adjust range to maintain maxPagesToShow
    if (endPage - startPage + 1 < maxPagesToShow) {
      if (currentPage - startPage >= halfRange) {
        startPage = Math.max(1, endPage - maxPagesToShow + 1);
      } else {
        endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);
      }
    }

    const range:any = [];

    // Add first page and ellipsis if necessary
    if (startPage > 1) {
      range.push(1);
      if (startPage > 2) {
        range.push('...');
      }
    }

    // Add pages in the calculated range
    for (let i = startPage; i <= endPage; i++) {
      range.push(i);
    }

    // Add last page and ellipsis if necessary
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        range.push('...');
      }
      range.push(totalPages);
    }

    return range;
  }

  const handlePageChange = (page, dataTable) => {
    const currentPage = pagination.pageIndex+1
    const totalPages = dataTable.getPageCount()
    if (page !== currentPage && page >= 1 && page <= totalPages) {
      dataTable.setPageIndex(page-1)
    }
  };

  const handleDisplayPageChange = (e) =>{
    setPagination({
      ...pagination,
      pageIndex: 0,
      pageSize: +e.target.value
    })
  }

  const generateTableData = async (flow) => {
    const paramsWithYear = {
      ...params,
      tradeflow:flow,
      productGroup: params.product_group,
      year:selectedYear,
      hsVer:params.hs_ver,
      hsDigits:selectedHSDigit.value
    }
    setDataTable([])
    setIsNotFound(false)
    try {
      const res = await getCommodities(paramsWithYear);
      const tradeData = res.data?.data?.rows || [];
      if(tradeData.length === 0){
        setIsNotFound(true);
      }
      const mappedData = tradeData.map(trade=>{
        return {
          hs: trade.hs,
          description: trade.description,
          val: trade.total_value,
          flow,
          hs_list:trade.hs_list || '-'
        }
      });
      setDataTable(mappedData);
    } catch(e) {
      setIsNotFound(true);
    }
  }

  useEffect(() => {
    if (!params?.rep) return;
    registerStart();
    setIsLoading(true);
    setIsNotFound(false);
    generateTableData(params.tradeflow).finally(() => {
      setIsLoading(false);
      registerEnd();
    });
  }, [selectedYear, selectedHSDigit, params, registerStart, registerEnd])

  const handleYearChange = (year) =>{
    setSelectedYear(+year)    
  }

  const handleHSDigitChange = (hsDigit) =>{
    setSelectedHSDigit(hsDigit)
  }

  const pagesTable = paginationRange(table);

  const displayHeader = table.getHeaderGroups().map(headerGroup=>(
    <tr className='fw-bold text-gray-800 py-4' key={headerGroup.id}>
    {
      headerGroup.headers.map(header=>{
        return (
          <th key={header.id} className={header.column.columnDef.meta?.headerClass}>
            <div 
              className={header.column.getCanSort()
                  ? 'cursor-pointer select-none'
                  : ''
              }
              title={
                header.column.getCanSort()
                  ? header.column.getNextSortingOrder() === 'asc'
                    ? 'Sort ascending'
                    : header.column.getNextSortingOrder() === 'desc'
                      ? 'Sort descending'
                      : 'Reset'
                  : undefined
              }
              onClick={header.column.getToggleSortingHandler()}>
                {flexRender(
                  header.column.columnDef.header,
                  header.getContext()
                )}
              <span className="ms-2"> 
                {{
                  asc: (<i className="fa-solid fa-sort-up"></i>),
                  desc: (<i className="fa-solid fa-sort-down"></i>),
                  false: (<i className="fa-solid fa-sort"></i>),
                }[header.column.getIsSorted() as string] ?? null}
              </span>
              </div>
          </th>
        )
      })
    }
    </tr>
  ))

  let cardBodyContent: React.ReactNode = null;
  
  if(!isLoading && dataTable?.length ){
    cardBodyContent = (
        <>
          <div className='table-responsive table-fixhead'>
            <table className='table table-row-bordered table-hover align-middle gs-0'>
              <thead>
                {displayHeader}
              </thead>
              <tbody className="gy-1 overflow-auto">
                {table.getRowModel().rows.map(row => (
                  <tr key={row.id}>
                      <td key={row.getVisibleCells()[0].id} className="text-center">
                        {flexRender(
                          row.getVisibleCells()[0].column.columnDef.cell, 
                          row.getVisibleCells()[0].getContext())
                        }
                      </td>
                      <td key={row.getVisibleCells()[1].id} className="text-start">
                        {flexRender(
                          row.getVisibleCells()[1].column.columnDef.cell, 
                          row.getVisibleCells()[1].getContext())
                        }
                      </td>
                      <td key={row.getVisibleCells()[2].id} className="text-end fw-bold px-5">
                        {flexRender(
                          row.getVisibleCells()[2].column.columnDef.cell, 
                          row.getVisibleCells()[2].getContext())
                        }
                      </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Row className='my-5' style={{ fontSize: '0.8em' }}>
            <Col md={4} className='d-flex justify-content-start align-items-center'>
              Display
              <Form.Select
                size="sm"
                className="w-80px mx-2"
                onChange={handleDisplayPageChange}
                value={pagination.pageSize}>
                <option value="10">10</option>
                <option value="20">20</option>
                <option value="50">50</option>
                <option value="100">100</option>
              </Form.Select>
              results
            </Col>
            <Col md={8} className='d-flex justify-content-end'>
              <Pagination size="sm">
                <Pagination.Prev
                  onClick={() => table.previousPage()}
                  disabled={!table.getCanPreviousPage()} />
                {/* Start Dynamic Page */}
                {pagesTable.map((page, index) => {
                  if (page === '...') {
                    return (
                      <Pagination.Ellipsis key={index} />
                    );
                  }
                  return (
                    <Pagination.Item
                      key={index}
                      active={page === pagination.pageIndex + 1}
                      onClick={() => handlePageChange(page, table)}
                    >
                      {page}
                    </Pagination.Item>
                  );
                })}
                {/* End Dynamic Page */}
                <Pagination.Next
                  onClick={() => table.nextPage()}
                  disabled={!table.getCanNextPage()} />
              </Pagination>
            </Col>
          </Row>
        </>
      )
  }else if(isNotFound){
    cardBodyContent = (
      <div className="d-flex align-items-center justify-content-center">
        <Alert variant="warning">
          <Alert.Heading>
            <i className="la la-exclamation-triangle" style={{ marginRight: '8px' }}></i>
            No Data Found
          </Alert.Heading>
            We couldn’t find any records for the selected reporter or partner country. 
            Please try again with a different selection or check your input for accuracy.
        </Alert>
      </div>
    );
  }else{
    cardBodyContent = (
      <div className="d-flex align-items-center justify-content-center" style={{ height: '50vh' }}>
        <Spinner animation="border" role="status" variant="primary" className="me-3" />
        <div className="loading-text">
          Loading
          <span className="dot">.</span>
          <span className="dot">.</span>
          <span className="dot">.</span>
        </div>
      </div>
    );
  }

  return (
  <Card className="card-custom">
    <Card.Header className="card-header-stretch d-flex align-items-center justify-content-between">
      <Card.Title
          title={cardTitle}
          style={{
            maxWidth: '60%',
          }}
        >
          <div className="d-flex flex-column">
            <span className="fw-bold">{mainTitle}</span>
            <span className="text-muted fs-7 fw-normal mt-1">{subTitle}</span>
          </div>
        </Card.Title>
      <div className="d-flex align-items-center justify-content-between gx-5">
        <Dropdown className="me-1">
          <Dropdown.Toggle variant="label-brand" id="" className="btn btn-sm btn-bold btn-label-brand dropdown-toggle fw-bold">
            {selectedHSDigit.label}
          </Dropdown.Toggle>

          <Dropdown.Menu>
            {
              HS_DIGITS.map(digit=>{
                return (<Dropdown.Item key={digit.value} onClick={() => handleHSDigitChange(digit)}>{digit.label}</Dropdown.Item>)
              })
            }
          </Dropdown.Menu>
        </Dropdown>
        <Dropdown>
          <Dropdown.Toggle variant="label-brand" id="" className="btn btn-sm btn-bold btn-label-brand dropdown-toggle fw-bold dss_graph_trade_partners_available_years_latest">
            {selectedYear}
          </Dropdown.Toggle>

          <Dropdown.Menu>
            {
              AVAILABLE_YEARS.map(year=>{
                return (<Dropdown.Item key={year} onClick={() => handleYearChange(year)}>{year}</Dropdown.Item>)
              })
            }
          </Dropdown.Menu>
        </Dropdown>
      </div>
    </Card.Header>
    <Card.Body style={{height:'600px'}}>
      {cardBodyContent}
    </Card.Body>
  </Card>
  )
}

export {TopCommodityTableCard}
